import React from 'react';
import { Link } from 'react-router-dom';
import { Flags } from 'react-feature-flags';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import MenuIcon from '@mui/icons-material/Menu';

import { useTranslation } from 'react-i18next';
import {
  RiHome2Line,
  RiUser3Line,
  RiFolderInfoLine,
  RiVideoLine,
  RiSettings5Line,
  RiHealthBookLine,
  RiLogoutBoxLine,
  RiExternalLinkLine,
} from 'react-icons/ri';

import { useAuth } from '@omnigenbiodata/ui';

import ROUTES from '../../../core/constants/routes.constant';
import { FEATURES } from '../../../core/constants/features.constants';
import { MICROSITE_URL } from '../../../core/constants/content.constant';
import { useStyles } from './component.styles';

interface SideMenuProps {
  isOpen: boolean;
  onToggle: (open: boolean) => (event: any) => void;
  showHealthOption?: boolean;
}

const chartColours = ['#F2AE21', '#5ABBE5', '#92C15F', '#12ACA2', '#D9D44F', '#EA516D', '#ED7723', '#B8B132'];

interface MenuItemIconProps {
  colour: string;
  children: React.ReactNode;
}

function MenuItemIcon({ colour, children }: MenuItemIconProps) {
  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${colour}22`,
        color: colour,
        border: 1,
        borderColor: `${colour}55`,
        flexShrink: 0,
        '& svg': {
          fontSize: '1.35rem',
        },
      }}
    >
      {children}
    </Box>
  );
}

interface NavItemProps {
  label: string;
  colour: string;
  icon: React.ReactNode;
  testId?: string;
  to?: string;
  href?: string;
  target?: string;
  onClick?: () => void;
}

function NavItem({ label, colour, icon, testId = 'nav-item', to, href, target, onClick }: NavItemProps) {
  const commonProps = href
    ? {
        component: 'a' as const,
        href,
        target,
      }
    : {
        component: Link,
        to: to || '',
      };

  return (
    <ListItem
      data-testid={testId}
      button
      key={label}
      onClick={onClick}
      {...commonProps}
      sx={{
        borderRadius: 2,
        mb: 0.75,
        px: 1.25,
        py: 1,
        color: 'text.primary',
        transition: 'background-color 150ms ease, transform 150ms ease',
        '&:hover': {
          bgcolor: `${colour}12`,
          transform: 'translateX(2px)',
        },
        '& .MuiListItemIcon-root': {
          minWidth: 50,
        },
      }}
    >
      <ListItemIcon>
        <MenuItemIcon colour={colour}>{icon}</MenuItemIcon>
      </ListItemIcon>

      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontWeight: 800,
          fontSize: '0.98rem',
        }}
      />
    </ListItem>
  );
}

function SideMenu({ isOpen, onToggle, showHealthOption = false }: SideMenuProps) {
  const { signOut } = useAuth();
  const { t } = useTranslation('portal');
  const { classes } = useStyles();

  const closeMenu = () => {
    onToggle(false);
  };

  return (
    <>
      <IconButton
        onClick={onToggle(true)}
        edge="start"
        className={classes.hamburger}
        data-testid="menu-btn"
        sx={{
          width: 48,
          height: 48,
          bgcolor: 'background.paper',
          color: 'text.primary',
          transition: 'background-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
          '&:hover': {
            bgcolor: 'grey.50',
            transform: 'translateY(-1px)',
            boxShadow: 4,
          },
        }}
      >
        <MenuIcon fontSize="large" />
      </IconButton>

      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onToggle(false)}
        data-testid="menu-drawer"
        PaperProps={{
          sx: {
            width: { xs: 320, sm: 360 },
            maxWidth: '88vw',
            borderTopRightRadius: 24,
            borderBottomRightRadius: 24,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box
          className={classes.drawer}
          role="presentation"
          onClick={onToggle(false)}
          onKeyDown={onToggle(false)}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              variant="overline"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 900,
                letterSpacing: '0.12em',
                lineHeight: 1,
              }}
            >
              {t('home.homeLink')}
            </Typography>

            <Typography
              variant="h6"
              component="p"
              sx={{
                mt: 0.75,
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              {t('home.yourPortalTitle')}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <List disablePadding>
              <NavItem
                label={t('home.homeLink')}
                colour={chartColours[0]}
                icon={<RiHome2Line />}
                to={ROUTES.portal}
                onClick={closeMenu}
              />

              {showHealthOption && (
                <Flags authorizedFlags={[FEATURES.EHR]}>
                  <NavItem
                    label={t('home.healthInfoLink')}
                    colour={chartColours[1]}
                    icon={<RiHealthBookLine />}
                    to={ROUTES.portalHealth}
                    onClick={closeMenu}
                  />
                </Flags>
              )}

              <Flags authorizedFlags={[FEATURES.USER_PROFILE]}>
                <NavItem
                  label={t('home.personalInfoLink')}
                  colour={chartColours[2]}
                  icon={<RiUser3Line />}
                  to={ROUTES.portalPersonal}
                  onClick={closeMenu}
                />
              </Flags>

              <Flags authorizedFlags={[FEATURES.USER_PREFERENCES]}>
                <NavItem
                  label={t('home.preferencesLink')}
                  colour={chartColours[3]}
                  icon={<RiSettings5Line />}
                  to={ROUTES.portalPrefs}
                  onClick={closeMenu}
                />
              </Flags>

              <Flags authorizedFlags={[FEATURES.PORTAL_PIS]}>
                <NavItem
                  label={t('home.pisLink')}
                  colour={chartColours[4]}
                  icon={<RiFolderInfoLine />}
                  to={ROUTES.portalInfo}
                  onClick={closeMenu}
                />
              </Flags>

              <Flags authorizedFlags={[FEATURES.PORTAL_VIDEO]}>
                <NavItem
                  label={t('home.videoLink')}
                  colour={chartColours[5]}
                  icon={<RiVideoLine />}
                  to={ROUTES.portalVideo}
                  onClick={closeMenu}
                />
              </Flags>

              <Divider sx={{ my: 1.5 }} />

              <Flags authorizedFlags={[FEATURES.PORTAL_FAQ]}>
                <NavItem
                  label={t('home.faqsLink')}
                  colour={chartColours[6]}
                  icon={<RiExternalLinkLine />}
                  href={`${MICROSITE_URL}#dmsaFaqsWrapper`}
                  target="_blank"
                  onClick={closeMenu}
                />
              </Flags>

              <NavItem
                label={t('home.contactLink')}
                colour={chartColours[7]}
                icon={<RiExternalLinkLine />}
                href={`${MICROSITE_URL}#contact`}
                target="_blank"
                onClick={closeMenu}
              />

              <Divider sx={{ my: 1.5 }} />

              <NavItem
                label={t('home.logoutLink')}
                colour={chartColours[5]}
                icon={<RiLogoutBoxLine />}
                testId="nav-logout"
                onClick={signOut}
              />
            </List>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

export default SideMenu;
